import { faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faCircleInfo, faHeartPulse, faRedo, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from "react";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import StepNav from "../../../../components/StepNav";
import moment from "moment";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";

const CounsellingForm = () => {
    const navigate = useNavigate();
    const axios = useAxiosPrivate();
    const { createInfo, auth, userPolicy, setShowPaymentRedirect } = useAuth();

    const [dayDelay] = useState(7);

    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [curStep, setCurStep] = useState(1)
    const [fetchingAvailability, setFetchingAvailability] = useState(false)
    const initialFetchDone = useRef(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [slotSelected, setSlotSelected] = useState(null)
    const [selectedDay, setSelectedDay] = useState(moment().add(dayDelay, 'days'))
    const [openSection, setOpenSection] = useState(null)
    const [reason, setReason] = useState('')
    const formattedPhoneNumber = useRef(null)
    const [mentalHealthCrisis, setMentalHealthCrisis] = useState(null);
    const [medicalSymptoms, setMedicalSymptoms] = useState(null);
    const [allSlots, setAllSlots] = useState([])

    const emergencySymptoms = [
        "Severe difficulty breathing",
        "Severe chest pain",
        "Severe abdominal pain",
        "Severe or uncontrolled bleeding",
        "Suspected heart attack or stroke",
        "An infection with dizziness, weakness or confusion (sepsis)",
        "Acute back pain with paralysis of the legs or difficulty using the bladder or bowel",
        "Unexplained loss of consciousness",
        "Uncontrolled seizures or fits",
        "You are pregnant and experiencing pain, bleeding or reduced movement of your baby",
    ];

    const morningSlots = allSlots.filter((slot) => {
        // Parse UTC time, convert to local time
        const time = moment(slot.time)
        const hour = time.hour()
        return hour >= 8 && hour < 12 && selectedDay.isSame(time, 'day')
    })

    const afternoonSlots = allSlots.filter((slot) => {
        const time = moment(slot.time)
        const hour = time.hour()
        return hour >= 12 && hour < 17 && selectedDay.isSame(time, 'day')
    })

    const eveningSlots = allSlots.filter((slot) => {
        const time = moment(slot.time)
        const hour = time.hour()
        return hour >= 17 && hour < 22 && selectedDay.isSame(time, 'day')
    })

    const fetchAvailability = async () => {
        try {
            if (fetchingAvailability) {
                return
            }
            setFetchingAvailability(true)
            const start = moment().add(dayDelay, 'days').format('YYYY-MM-DD')
            const end = moment().add(dayDelay + 2, 'days').format('YYYY-MM-DD')

            const response = await axios.post(process.env.REACT_APP_WBA_API_URL + '/availabilities/9/' + start + '/' + end + '?api_token=' + process.env.REACT_APP_WBA_API_KEY)
            if (!response.data.error) {
                setAllSlots(response.data.result)
            } else {
                createInfo('error', 'Failed to fetch availabilities')
            }
        } catch (error) {
            createInfo('error', 'Failed to fetch availabilities')
        } finally {
            setFetchingAvailability(false)
        }
    }
    const location = useLocation()
    const product = location?.state?.product
    const [hasAccess, setHasAccess] = useState(true)
    const [accessMode, setAccessMode] = useState('fallback') // 'api' | 'fallback'
    const [accessByProduct, setAccessByProduct] = useState({})

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            name: auth?.user?.name || "",
            email: auth?.user?.email || "",
            dob: auth?.user?.date_of_birth || "",
            contact: auth?.user?.phone_number || "",
        }));
    }, [auth?.user]);


    useEffect(() => {
        setHasAccess(checkProductAccess(product?.title))
    }, [])

    useEffect(() => {
        if (!auth?.token) return
        let cancelled = false
        setAccessMode('fallback')
        axios
            .post('/service-access/check', {
                product_titles: [product.title],
                ...(process.env.REACT_APP_ENV !== 'production'
                    ? {
                        policy_override: {
                            product: userPolicy?.product,
                            status: userPolicy?.status,
                            department: userPolicy?.department,
                        },
                    }
                    : {}),
            })
            .then((response) => {
                if (cancelled) return
                const byProduct = response?.data?.result?.by_product
                if (byProduct && typeof byProduct === 'object') {
                    setAccessByProduct(byProduct)
                    setAccessMode('api')
                }
            })
            .catch(() => {
                if (cancelled) return
                setAccessMode('fallback')
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.token, userPolicy?.id])

    // Process payment - placeholder function
    const processPayment = (product, enquriy) => {
        if (!!product?.paymentLink) {
            // Append the parameter to the URL
            const finalUrl = `${product.paymentLink}?client_reference_id=${enquriy}`;

            window.location.href = finalUrl;
        } else {
            createInfo('error', 'Payment link not available.');
        }
    }

    // Check overall service access based on user policy
    const checkOverallServiceAccess = () => {
        return !(
            ['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.product) ||
            !['live', 'expiring'].includes(userPolicy?.status)
        )
    }

    // Check access for specific products
    const checkProductAccess = (productTitle) => {
        console.log(accessMode)
        if (accessMode === 'api') {
            return !!accessByProduct?.[productTitle]
        }

        // First check overall access
        if (!checkOverallServiceAccess()) return false

        if (['SAS', 'Mutual'].includes(userPolicy?.department)) return true
        if (['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.department)) return false

        if (![
            'Counselling',
            'Mindfulness',
            'Relaxation Room Live',
            'Nurse Support',
            'Menopause Support',
            'GP Services',
            'SAS Gym',
            'Happier Healthier You',
            'HHY Community',
            'Physiotherapy',
            'Preparing Parents',
            'Food Intolerance Testing',
            'Financial Wellbeing',
            'Manspace',
            'Pupil Yoga',
            'Pastoral Resources',
            'Awards & Programmes',
            'Ask the PT'
        ].includes(productTitle)) return false

        switch (productTitle) {
            case 'Counselling':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'GP Services':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Happier Healthier You':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Physiotherapy':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Preparing Parents':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Pupil Counselling':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            default:
                return true
        }
    }

    const goBack = () => {
        if (curStep === 1) {
            navigate(-1)
        } else {
            setCurStep(curStep - 1)
        }
    }

    const handleChange = (e, fieldName) => {
        const value =
            e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
    };

    const resolvedName = formData.name || auth?.user?.name || '';
    const resolvedEmail = formData.email ?? auth?.user?.email ?? '';
    const resolvedDob = formData.dob || auth?.user?.date_of_birth || '';
    const resolvedSchoolName = formData.schoolName || auth?.user?.client?.organisation?.name || '';
    const resolvedSchoolPostcode = formData.schoolPostcode || auth?.user?.client?.organisation?.postcode || '';

    const formattedDob = (() => {
        const rawDob = resolvedDob;
        const [year, month, day] = String(rawDob).split("-");
        if (year && month && day) {
            return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year).padStart(4, "0")}`;
        }
        return rawDob;
    })();

    const validateStep = () => {
        switch (curStep) {
            case 1:
                return !(mentalHealthCrisis || medicalSymptoms) &&
                    medicalSymptoms !== null &&
                    mentalHealthCrisis !== null
            case 2:
                return resolvedName &&
                    resolvedName.trim() !== "" &&
                    resolvedEmail &&
                    resolvedEmail.trim() !== "" &&
                    resolvedDob &&
                    resolvedDob.trim() !== "" &&
                    formData.contact &&
                    formData.contact.trim() !== "" &&
                    formData.confirm &&
                    formattedPhoneNumber.current &&
                    validateEmail(resolvedEmail.trim());
            case 3:
                return slotSelected !== null
            default:
                return false
        }
    }

    const handleNextStep = async () => {
        switch (curStep) {
            case 1:
            case 2:
                if (!fetchingAvailability && !initialFetchDone.current) {
                    fetchAvailability()
                    initialFetchDone.current = true
                }
                setCurStep(curStep + 1)
                break
            case 3:
                setSubmitting(true);

                try {
                    const infoObj = {
                        user: {
                            name: resolvedName,
                            email: resolvedEmail,
                            phone_number: formattedPhoneNumber.current,
                            dob: resolvedDob,
                        },
                        school: {
                            schoolName: resolvedSchoolName,
                            schoolPostcode: resolvedSchoolPostcode,
                        },
                        contact: formData.bestTime || "",
                        extras: "",
                    };

                    // Handle specific fields like stillInWork
                    if (formData.stillInWork) {
                        infoObj.extras = "This staff member is currently working.";
                    }

                    const data = {
                        info: JSON.stringify(infoObj),
                        name: 'Mental Health',
                        product: "counselling",
                        type: 'staff',
                        staff_id: auth?.user?.client?.staff_id,
                        policy_id: userPolicy?.id,
                        policy_reference: userPolicy?.reference,
                        user_id: auth?.user?.id,
                        client_id: auth?.user?.client_id,
                        counselling_v2: true,
                        ...slotSelected
                    }

                    const response = await axios.post('/tasks/create-from-enquiry', data)

                    if (response.data.error) {
                        switch (response.data.message) {
                            case 'Ongoing Enquiry Exists':
                                setErrorMsg("Enquiry Already Exists");
                                break
                            case 'Slot unavailable':
                                setErrorMsg("We're sorry, this slot is no longer available, please try again");
                                break
                            case 'Failed to reserve slot':
                            default:
                                setErrorMsg('Failed to create request');
                                break
                        }
                    } else if (!response.data.error) {
                        setErrorMsg('')
                        createInfo('success', "Submitted Successfully.");
                    } else {
                        setErrorMsg('Failed to create request')
                    }
                } catch (error) {
                    console.error(error)
                    setErrorMsg('Failed to create request')
                } finally {
                    setSubmitting(false)
                }
                break
            default:
                break
        }
    }

    const validateAndFormatPhoneNumber = (phoneNumber) => {
        const phoneNumberUtil = PhoneNumberUtil.getInstance()

        try {
            const parsedNumber = phoneNumberUtil.parse(phoneNumber, 'GB')

            if (!phoneNumberUtil.isValidNumber(parsedNumber)) {
                formattedPhoneNumber.current = null
                return false
            }

            formattedPhoneNumber.current = phoneNumberUtil.format(
                parsedNumber,
                PhoneNumberFormat.E164
            )
            return true
        } catch (e) {
            console.error(e)
            formattedPhoneNumber.current = null
            return false
        }
    }

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    }


    return (
        <section>
            <div className="max-w-4xl mx-auto p-6 space-y-8 text-portal-dark-blue">
                <StepNav currentStep={curStep} totalSteps={3} stepHeaders={['Counselling', 'Counselling - Contact Details', 'Counselling - Select a Time']} />
                <section className="pb-6">
                    {curStep === 1 ? (
                        <>
                            <div className="border border-blue-400 bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3">
                                <FontAwesomeIcon icon={faCircleInfo} className="mt-1 text-lg" />
                                <p className="text-sm">
                                    You must not use this service for emergencies or urgent
                                    conditions, as this may delay necessary treatment.
                                </p>
                            </div>

                            <div className="text-[#41264B] text-lg font-semibold mt-4">
                                How it works
                            </div>
                            <p className="mt-2 text-[#41264B] whitespace-pre-line">
                                Our BACP registered counsellors provide confidential, individual
                                therapy sessions which are delivered safely and appropriately
                                and available whether you are still working in school or absent
                                through illness. The need for therapy does not need to be work
                                related.
                            </p>

                            <div className="mb-4">
                                <h2 className="font-bold text-lg mb-3 mt-4">
                                    Are you experiencing a mental health crisis or in suicidal distress?
                                </h2>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="mentalCrisis"
                                            value="no"
                                            onChange={() => setMentalHealthCrisis(false)}
                                        />{" "}
                                        No
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="mentalCrisis"
                                            value="yes"
                                            onChange={() => setMentalHealthCrisis(true)}
                                        />{" "}
                                        Yes
                                    </label>
                                </div>
                            </div>

                            <div>
                                <h2 className="font-bold text-lg mb-3">
                                    Are you experiencing any of the following medical symptoms?
                                </h2>
                                <div className="bg-[#F9F9F9] p-4 rounded-lg border">
                                    <ul className="space-y-2 text-sm text-wba-dark-pink">
                                        {emergencySymptoms.map((symptom, index) => (
                                            <li key={index} className="flex items-start gap-2">
                                                <FontAwesomeIcon icon={faHeartPulse} className="mt-1 " />
                                                <span>{symptom}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="flex gap-6 mt-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="symptoms"
                                            value="no"
                                            onChange={() => setMedicalSymptoms(false)}
                                        />{" "}
                                        No
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="symptoms"
                                            value="yes"
                                            onChange={() => setMedicalSymptoms(true)}
                                        />{" "}
                                        Yes
                                    </label>
                                </div>
                            </div>

                            {(mentalHealthCrisis || medicalSymptoms) && (
                                <div className="border border-red-500 bg-red-50 text-red-700 p-4 rounded-lg flex items-start gap-3 mt-4">
                                    <FontAwesomeIcon
                                        icon={faTriangleExclamation}
                                        className="mt-1 text-lg text-red-600"
                                    />
                                    <p className="text-sm">
                                        Call 999 in the UK or 112 in Europe now. If you are abroad, please
                                        contact your local emergency services immediately.
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (curStep === 2) ? (
                        <>
                            <p className="mt-2 text-[#41264B] whitespace-pre-line text-center">
                                Complete the form below to make your request for Counselling. All requests are confidential.
                            </p>
                            <form className="space-y-4 text-[#41264B] p-4 rounded-md mt-6">
                                {/* Name Field - Read Only */}
                                <div>
                                    <label className="block text-sm/6 font-semibold">Name</label>
                                    <p className="mt-1 text-base text-gray-900 sm:text-sm/6">{formData.name || ''}</p>
                                </div>

                                {/* Date of Birth Field - Read Only */}
                                <div>
                                    <label className="block text-sm/6 font-semibold">
                                        Date Of Birth
                                    </label>
                                    <p className="mt-1 text-base text-gray-900 sm:text-sm/6">
                                        {formattedDob}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Preferred Contact Email for Appointments
                                        <span className="text-red-600">*</span>
                                    </label>

                                    <input
                                        type="email"
                                        className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                                        placeholder="Email Address"
                                        value={formData.email || ""}
                                        onChange={(e) => handleChange(e, "email")}
                                    />

                                    {formData.email && validateEmail(formData.email) && (
                                        <p className="mt-1 text-sm text-green-600">
                                            Valid email address
                                        </p>
                                    )}

                                    {formData.email && !validateEmail(formData.email) && (
                                        <p className="mt-1 text-sm text-red-600">
                                            Please enter a valid email address.
                                        </p>
                                    )}
                                </div>


                                {/* Contact Number Field */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Contact Number <span className="text-red-600">*</span>
                                    </label>

                                    <div className="flex w-full rounded border shadow-sm overflow-hidden">
                                        <span className="flex items-center px-3 bg-gray-100 text-[#41264B] font-medium">
                                            +44
                                        </span>

                                        <input
                                            type="tel"
                                            className="flex-1 p-2 text-[#41264B] outline-none"
                                            placeholder="01234567890"
                                            value={formData.contact || ""}
                                            maxLength={11}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                    .replace(/\D/g, "")
                                                    .slice(0, 11);

                                                handleChange(
                                                    { target: { value } },
                                                    "contact"
                                                );
                                            }}
                                        />
                                    </div>

                                    {formData.contact && validateAndFormatPhoneNumber(formData.contact) && (
                                        <p className="mt-1 text-sm text-green-600">
                                            Valid UK telephone number
                                        </p>
                                    )}

                                    {formData.contact && !validateAndFormatPhoneNumber(formData.contact) && (
                                        <p className="mt-1 text-sm text-red-600">
                                            Please enter a valid UK telephone number.
                                        </p>
                                    )}
                                </div>


                                {/* Still In Work Checkbox */}
                                <div className="flex items-center space-x-2">
                                    <input
                                        name="stillInWork"
                                        id="stillInWork"
                                        type="checkbox"
                                        checked={!!formData.stillInWork}
                                        onChange={(e) => handleChange(e, "stillInWork")}
                                    />
                                    <label htmlFor="stillInWork" className="text-sm">
                                        Please tick if you are still in work
                                    </label>
                                </div>

                                {/* Confirmation Checkbox */}
                                <div className="flex items-center space-x-2">
                                    <input
                                        name="confirm"
                                        id="confirm"
                                        type="checkbox"
                                        checked={!!formData.confirm}
                                        onChange={(e) => handleChange(e, "confirm")}
                                    />
                                    <label htmlFor="confirm" className="text-sm">
                                        By Requesting a service, I am giving my permission for
                                        School Wellbeing and third-party service providers to
                                        contact me. For more information on how we collect and
                                        manage your data, please don't hesitate to contact School
                                        Wellbeing on 01773814404 <span className="text-red-600">*</span>
                                    </label>
                                </div>
                                {!validateStep() && Object.keys(formData).length > 0 && (
                                    <p className="text-sm text-red-600">
                                        Please fill in all required fields before continuing.
                                    </p>
                                )}
                            </form>
                        </>
                    ) : (
                        <div className="p-4 flex-1">
                            <div className="text-center font-semibold mb-4">
                                Please select an appointment slot from the list below
                            </div>

                            <div className="flex my-5 justify-center gap-2">
                                <div className="font-semibold">
                                    Slot Selected:
                                </div>
                                {slotSelected?.time ? (
                                    <div className="">
                                        {moment(slotSelected?.time).format('dddd Do MMMM HH:mm') ?? ''}
                                    </div>
                                ) : (
                                    <div className="">
                                        None
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-center">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {[moment().add(dayDelay, 'days'), moment().add(dayDelay + 1, 'days'), moment().add(dayDelay + 2, 'days')].map((item, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => setSelectedDay(item)}
                                            className={`border rounded-lg py-2 font-medium transition px-10
                                                ${selectedDay.isSame(item, 'day')
                                                    ? 'bg-light-purple border-light-purple'
                                                    : 'bg-white'
                                                }`}
                                        >
                                            {item.format('ddd DD MMM')}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {errorMsg && (
                                <div className="text-center mt-5 text-[#ff0000]">
                                    {errorMsg}
                                </div>
                            )}

                            <div className="flex-col mt-5 space-y-6">
                                {[{ slots: morningSlots, section: 'Morning' }, { slots: afternoonSlots, section: 'Afternoon' }, { slots: eveningSlots, section: 'Evening' }].map(({ slots, section }) => {
                                    return (
                                        <div className="border rounded-lg overflow-hidden">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    setOpenSection(openSection === section ? '' : section)
                                                }}
                                                className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100"
                                            >
                                                <span className="font-medium">
                                                    {section} {fetchingAvailability
                                                        ? <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin" />
                                                        : `(${slots.length} available)`}
                                                </span>

                                                <FontAwesomeIcon
                                                    icon={openSection === section ? faChevronUp : faChevronDown}
                                                    className="text-gray-500"
                                                />
                                            </button>

                                            {openSection === section && (
                                                <div className="p-4 bg-white">
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {!fetchingAvailability && slots.map((slot, index) => (
                                                            <button
                                                                type="button"
                                                                key={index}
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    setSlotSelected(slot)
                                                                }}
                                                                className={`rounded-md py-2 px-3 text-sm font-medium border ${slotSelected === slot
                                                                        ? 'bg-light-purple border-light-purple text-white'
                                                                        : 'bg-white hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                {moment(slot.time).format('HH:mm')}
                                                            </button>
                                                        ))}
                                                        {fetchingAvailability && (
                                                            <div>
                                                                Loading...
                                                            </div>
                                                        )}
                                                        {!fetchingAvailability && slots?.length === 0 && (
                                                            <div>No Slots Available</div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    <div className={`flex items-center justify-between mt-5 ${curStep === 1 ? '' : 'max-w-[51rem] mx-auto'}`}>
                        <button
                            type="button"
                            onClick={goBack}
                            disabled={submitting}
                            className={`px-8 py-2 bg-gray-600 text-white rounded-md shadow-sm hover:bg-gray-700 mt-4 ${submitting ? 'cursor-not-allowed opacity-50' : ''
                                }`}
                        >
                            {curStep === 1 ? 'Cancel' : 'Back'}
                        </button>
                        <button
                            type="submit"
                            disabled={!validateStep() || submitting}
                            onClick={() => handleNextStep()}
                            className={`px-10 py-2 rounded-md shadow-sm text-white mt-4 transition-all ${!validateStep() || submitting
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-portal-light hover:bg-portal-medium'
                                }`}
                        >
                            {curStep === 1
                                ? 'Request Help'
                                : curStep === 3
                                    ? 'Submit Request'
                                    : 'Continue'}
                            {submitting && (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    className="ml-2 animate-spin"
                                />
                            )}
                        </button>
                    </div>
                </section>

            </div>
        </section>
    );
};

export default CounsellingForm;









